import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { Heart, Loader2, Home } from 'lucide-react';
import { motion } from 'framer-motion';
import { getHearts, getStats } from '@/lib/api';
import { cityConfig } from '@/config/cities';
import 'leaflet/dist/leaflet.css';

const CITY_CENTER = cityConfig.mapCenter;

const HeartMarker = ({ isVerified }) => {
  if (isVerified) {
    return (
      <Heart
        className="w-6 h-6 text-[#E63946] fill-[#E63946]"
        style={{ filter: 'drop-shadow(0 0 8px rgba(230, 57, 70, 0.8))' }}
      />
    );
  }
  return (
    <Heart
      className="w-6 h-6"
      style={{
        color: '#FFFFFF',
        fill: '#FFFFFF',
        filter: 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.8))',
      }}
    />
  );
};

const MapView = () => {
  const navigate = useNavigate();
  const [hearts, setHearts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heartsData, statsData] = await Promise.all([getHearts(), getStats()]);
        setHearts(heartsData);
        setStats(statsData);
      } catch (e) {
        console.error('Failed to fetch map data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const heartsWithCoords = useMemo(() =>
    hearts.filter(h => h.latitude && h.longitude),
    [hearts]
  );

  const createIcon = (isVerified) => divIcon({
    html: renderToStaticMarkup(<HeartMarker isVerified={isVerified} />),
    className: 'custom-heart-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 56px - 80px)' }} data-testid="map-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  const pct = stats ? (stats.total_discovered / cityConfig.totalHearts) * 100 : 0;

  return (
    <div className="relative w-full z-30" style={{ height: 'calc(100vh - 56px - 80px)' }} data-testid="map-page">
      {/* Top gauge */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-2 left-2 right-2 z-[1000] pointer-events-none"
      >
        <div className="max-w-sm mx-auto bg-[#0A0A0B]/85 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-3 border border-[#27272A]/50">
          <Heart className="w-3.5 h-3.5 text-[#E63946] flex-shrink-0" fill="#E63946" />
          <div className="flex-1 h-2 bg-[#27272A] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#E63946] to-[#ff6b7a] rounded-full transition-all duration-500"
              style={{ width: `${Math.max(pct, 1)}%` }}
            />
          </div>
          {stats && (
            <span className="text-xs font-bold text-white whitespace-nowrap">
              <span className="text-[#E63946]">{stats.total_discovered}</span>
              <span className="text-[#71717A]"> / {cityConfig.totalHearts}</span>
            </span>
          )}
        </div>
      </motion.div>

      {/* Legend */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="absolute top-14 right-2 z-[1000] pointer-events-none"
      >
        <div className="bg-[#0A0A0B]/85 backdrop-blur-md rounded-lg px-2.5 py-1.5 border border-[#27272A]/50 flex gap-3">
          <div className="flex items-center gap-1.5 text-[10px] text-[#A1A1AA]">
            <span className="w-2 h-2 rounded-full bg-[#E63946] inline-block" />
            <span>Vérifié</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-[#A1A1AA]">
            <span className="w-2 h-2 rounded-full bg-white inline-block" />
            <span>Déclaré</span>
          </div>
        </div>
      </motion.div>

      {/* Map — hauteur absolue forcée */}
      <MapContainer
        center={CITY_CENTER}
        zoom={cityConfig.mapZoom}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        zoomControl={true}
        attributionControl={false}
      >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution=""
            className="hearts-dark-tiles"
            maxZoom={19}
          />
          {heartsWithCoords.map((heart) => (
            <Marker
              key={heart.id}
              position={[heart.latitude, heart.longitude]}
              icon={createIcon(heart.verification_status === 'verified')}
              eventHandlers={{
                click: () => navigate(`/heart/${heart.heart_number}`)
              }}
            >
              <Popup>
                <div className="text-center p-1">
                  <p className="font-bold text-lg text-[#E63946]">#{heart.heart_number}</p>
                  <p className="text-sm">par {heart.pseudo}</p>
                  {heart.location_name && (
                    <p className="text-xs text-gray-400 mt-1">{heart.location_name}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

      {/* Bouton Accueil flottant — centré, en bas */}
      <div className="absolute left-0 right-0 z-[1000] flex justify-center bottom-3">
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          onClick={() => navigate('/')}
          className="bg-[#E63946] text-white font-bold text-xs tracking-wider uppercase px-5 py-2.5 rounded-full flex items-center gap-2 shadow-lg"
          style={{ boxShadow: '0 4px 20px rgba(230, 57, 70, 0.5)' }}
          data-testid="map-home-button"
        >
          <Home className="w-3.5 h-3.5" />
          Accueil
        </motion.button>
      </div>
    </div>
  );
};

export default MapView;
